import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import { Layout } from '../components/Layout';
import { Plus, Pencil, Trash2, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const fmtBRL = (n) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(n || 0);

const emptyPlan = () => ({
  name: '',
  price_brl: 0,
  duration_days: 30,
  is_active: true,
  is_trial: false,
  max_apoiadores: '',
  max_liderancas: '',
  feature_mobilizacao: true,
  feature_financeiro: false,
  feature_heatmap: false,
  feature_network: false,
  destaque: false,
  ordem: 10,
});

export const PlatformPlansPage = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyPlan());

  const fetchPlans = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/platform/plans`, { withCredentials: true });
      setPlans(data || []);
    } catch (_e) {
      toast.error('Erro ao carregar planos');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { fetchPlans(); }, []);

  const openCreate = () => { setEditing(null); setForm(emptyPlan()); setOpen(true); };
  const openEdit = (p) => {
    setEditing(p.id);
    setForm({
      ...p,
      max_apoiadores: p.max_apoiadores ?? '',
      max_liderancas: p.max_liderancas ?? '',
    });
    setOpen(true);
  };

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    const payload = {
      ...form,
      max_apoiadores: form.max_apoiadores === '' ? null : parseInt(form.max_apoiadores, 10),
      max_liderancas: form.max_liderancas === '' ? null : parseInt(form.max_liderancas, 10),
      price_brl: parseFloat(form.price_brl) || 0,
      duration_days: parseInt(form.duration_days, 10) || 30,
      ordem: parseInt(form.ordem, 10) || 0,
    };
    try {
      if (editing) {
        await axios.put(`${BACKEND_URL}/api/platform/plans/${editing}`, payload, { withCredentials: true });
        toast.success('Plano atualizado.');
      } else {
        await axios.post(`${BACKEND_URL}/api/platform/plans`, payload, { withCredentials: true });
        toast.success('Plano criado.');
      }
      setOpen(false);
      fetchPlans();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao salvar');
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Excluir plano "${p.name}"?`)) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/platform/plans/${p.id}`, { withCredentials: true });
      toast.success('Plano excluído.');
      fetchPlans();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao excluir');
    }
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="platform-plans-page">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-3xl font-black tracking-tight">Planos da plataforma</h1>
            <p className="text-muted-foreground mt-1">
              Configure preços, limites e funcionalidades de cada plano (Trial, Básico, Pro e custom).
            </p>
          </div>
          <Button onClick={openCreate} data-testid="new-plan-btn">
            <Plus className="h-4 w-4 mr-2" /> Novo plano
          </Button>
        </div>

        {loading ? (
          <p className="py-12 text-center text-muted-foreground">Carregando...</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {plans.map((p) => (
              <Card key={p.id} className={p.destaque ? 'border-emerald-500' : ''} data-testid={`plan-${p.code}`}>
                <CardContent className="pt-6 space-y-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-bold text-lg">{p.name}</h3>
                        {p.is_trial && <Badge variant="outline" className="bg-blue-50 text-blue-700">Trial</Badge>}
                        {p.destaque && <Badge className="bg-emerald-500">Destaque</Badge>}
                        {!p.is_active && <Badge variant="outline" className="bg-slate-100">Inativo</Badge>}
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">id: {p.id}</p>
                    </div>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="icon" onClick={() => openEdit(p)} data-testid={`edit-plan-${p.code}`}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost" size="icon"
                        className="text-destructive"
                        onClick={() => remove(p)}
                        data-testid={`delete-plan-${p.code}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  <div>
                    <div className="font-bold text-2xl">{fmtBRL(p.price_brl)}</div>
                    <div className="text-xs text-muted-foreground">a cada {p.duration_days} dias</div>
                  </div>
                  <div className="space-y-1 text-sm">
                    <Row label="Apoiadores" value={p.max_apoiadores ? p.max_apoiadores.toLocaleString('pt-BR') : 'Ilimitados'} />
                    <Row label="Lideranças" value={p.max_liderancas ? p.max_liderancas.toLocaleString('pt-BR') : 'Ilimitadas'} />
                    <Row label="Mobilização" value={p.feature_mobilizacao ? '✓' : '—'} />
                    <Row label="Financeiro" value={p.feature_financeiro ? '✓' : '—'} />
                    <Row label="Heatmap" value={p.feature_heatmap ? '✓' : '—'} />
                    <Row label="Network" value={p.feature_network ? '✓' : '—'} />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Modal */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? 'Editar plano' : 'Novo plano'}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
            <div className="md:col-span-4">
              <Label>Nome</Label>
              <Input value={form.name} onChange={(e) => update('name', e.target.value)} data-testid="plan-name" />
            </div>
            <div className="md:col-span-2">
              <Label>Ordem</Label>
              <Input type="number" value={form.ordem} onChange={(e) => update('ordem', e.target.value)} />
            </div>
            <div className="md:col-span-3">
              <Label>Preço (R$)</Label>
              <Input type="number" step="0.01" value={form.price_brl} onChange={(e) => update('price_brl', e.target.value)} data-testid="plan-price" />
            </div>
            <div className="md:col-span-3">
              <Label>Duração (dias)</Label>
              <Input type="number" value={form.duration_days} onChange={(e) => update('duration_days', e.target.value)} />
            </div>
            <div className="md:col-span-3">
              <Label>Máx. apoiadores (vazio = ilimitado)</Label>
              <Input type="number" value={form.max_apoiadores} onChange={(e) => update('max_apoiadores', e.target.value)} placeholder="Ilimitado" />
            </div>
            <div className="md:col-span-3">
              <Label>Máx. lideranças (vazio = ilimitado)</Label>
              <Input type="number" value={form.max_liderancas} onChange={(e) => update('max_liderancas', e.target.value)} placeholder="Ilimitado" />
            </div>
            <div className="md:col-span-6 grid grid-cols-2 md:grid-cols-3 gap-3 pt-2 border-t">
              <FeatureToggle label="Mobilização" value={form.feature_mobilizacao} onChange={(v) => update('feature_mobilizacao', v)} />
              <FeatureToggle label="Financeiro" value={form.feature_financeiro} onChange={(v) => update('feature_financeiro', v)} />
              <FeatureToggle label="Heatmap" value={form.feature_heatmap} onChange={(v) => update('feature_heatmap', v)} />
              <FeatureToggle label="Network graph" value={form.feature_network} onChange={(v) => update('feature_network', v)} />
              <FeatureToggle label="Ativo" value={form.is_active} onChange={(v) => update('is_active', v)} />
              <FeatureToggle label="Destacar (Pro)" value={form.destaque} onChange={(v) => update('destaque', v)} icon={Sparkles} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={save} data-testid="save-plan-btn">{editing ? 'Salvar' : 'Criar'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

const Row = ({ label, value }) => (
  <div className="flex justify-between text-sm">
    <span className="text-muted-foreground">{label}</span>
    <span className="font-medium">{value}</span>
  </div>
);

const FeatureToggle = ({ label, value, onChange, icon: Icon }) => (
  <div className="flex items-center justify-between border border-slate-200 rounded-lg p-3">
    <div className="flex items-center gap-2">
      {Icon && <Icon className="h-3.5 w-3.5 text-emerald-600" />}
      <span className="text-sm font-medium">{label}</span>
    </div>
    <Switch checked={!!value} onCheckedChange={onChange} />
  </div>
);
